/**
 * Referee Hooks
 * Manages referee panel data and actions
 *
 * Features:
 * - Profile with balance and stats
 * - Commission history (balance log)
 * - Withdrawal history and creation
 * - Token conversion (credit to any user's game account)
 */

import { useCallback, useState } from 'react';
import { useApi, useMutation } from './useApi';
import { refereeAPI } from '@/services/api';
import type {
  RefereeProfileResponse,
  RefereeBalanceLogResponse,
  RefereeBalanceLogEntry,
  RefereeWithdrawalsResponse,
  RefereeWithdrawal,
  CreateRefereeWithdrawalResponse,
  TokenConversionResponse,
  RefereeConversionsResponse,
  RefereeConversion,
} from '@/types';

// =============================================================================
// Profile Hook
// =============================================================================

export interface UseRefereeProfileOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: RefereeProfileResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching referee profile
 */
export function useRefereeProfile(options: UseRefereeProfileOptions = {}) {
  const { fetchOnMount = true, onSuccess, onError } = options;

  const api = useApi<RefereeProfileResponse>(
    () => refereeAPI.getProfile(),
    { fetchOnMount, onSuccess, onError }
  );

  return {
    ...api,
    profile: api.data?.referee ?? null,
    refetch: api.execute,
  };
}

// =============================================================================
// Balance Log Hook (Commission History)
// =============================================================================

export interface UseBalanceLogOptions {
  fetchOnMount?: boolean;
  limit?: number;
  onSuccess?: (data: RefereeBalanceLogResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching commission history with pagination
 */
export function useBalanceLog(options: UseBalanceLogOptions = {}) {
  const { fetchOnMount = false, limit = 50, onSuccess, onError } = options;

  const [logs, setLogs] = useState<RefereeBalanceLogEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const api = useApi<RefereeBalanceLogResponse, [number, number]>(
    (lim, off) => refereeAPI.getBalanceLog({ limit: lim, offset: off }),
    {
      onSuccess: (data) => {
        if (offset === 0) {
          setLogs(data.logs);
        } else {
          setLogs((prev) => [...prev, ...data.logs]);
        }
        setHasMore(logs.length + data.logs.length < data.total);
        onSuccess?.(data);
      },
      onError,
    }
  );

  const fetch = useCallback(() => {
    setOffset(0);
    return api.execute(limit, 0);
  }, [api, limit]);

  const fetchMore = useCallback(() => {
    if (!hasMore || api.isLoading) return;
    const newOffset = offset + limit;
    setOffset(newOffset);
    return api.execute(limit, newOffset);
  }, [api, limit, offset, hasMore]);

  const refetch = useCallback(() => {
    setOffset(0);
    setLogs([]);
    return api.execute(limit, 0);
  }, [api, limit]);

  // Initial fetch
  if (fetchOnMount && logs.length === 0 && !api.isLoading && !api.isSuccess) {
    fetch();
  }

  return {
    logs,
    total: api.data?.total ?? 0,
    hasMore,
    isLoading: api.isLoading,
    error: api.error,
    fetch,
    fetchMore,
    refetch,
  };
}

// =============================================================================
// Withdrawals Hook
// =============================================================================

export interface UseRefereeWithdrawalsOptions {
  fetchOnMount?: boolean;
  limit?: number;
  onSuccess?: (data: RefereeWithdrawalsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching referee withdrawal history
 */
export function useRefereeWithdrawals(options: UseRefereeWithdrawalsOptions = {}) {
  const { fetchOnMount = false, limit = 50, onSuccess, onError } = options;

  const [withdrawals, setWithdrawals] = useState<RefereeWithdrawal[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const api = useApi<RefereeWithdrawalsResponse, [number, number]>(
    (lim, off) => refereeAPI.getWithdrawals({ limit: lim, offset: off }),
    {
      onSuccess: (data) => {
        if (offset === 0) {
          setWithdrawals(data.withdrawals);
        } else {
          setWithdrawals((prev) => [...prev, ...data.withdrawals]);
        }
        setHasMore(withdrawals.length + data.withdrawals.length < data.total);
        onSuccess?.(data);
      },
      onError,
    }
  );

  const fetch = useCallback(() => {
    setOffset(0);
    return api.execute(limit, 0);
  }, [api, limit]);

  const fetchMore = useCallback(() => {
    if (!hasMore || api.isLoading) return;
    const newOffset = offset + limit;
    setOffset(newOffset);
    return api.execute(limit, newOffset);
  }, [api, limit, offset, hasMore]);

  const refetch = useCallback(() => {
    setOffset(0);
    setWithdrawals([]);
    return api.execute(limit, 0);
  }, [api, limit]);

  // Initial fetch
  if (fetchOnMount && withdrawals.length === 0 && !api.isLoading && !api.isSuccess) {
    fetch();
  }

  return {
    withdrawals,
    total: api.data?.total ?? 0,
    hasMore,
    isLoading: api.isLoading,
    error: api.error,
    fetch,
    fetchMore,
    refetch,
  };
}

// =============================================================================
// Create Withdrawal Hook
// =============================================================================

export interface UseCreateWithdrawalOptions {
  onSuccess?: (data: CreateRefereeWithdrawalResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating referee withdrawal
 */
export function useCreateRefereeWithdrawal(options: UseCreateWithdrawalOptions = {}) {
  const mutation = useMutation<CreateRefereeWithdrawalResponse, [number, string]>(
    (amount, cardNumber) => refereeAPI.createWithdrawal({ amount, cardNumber }),
    options
  );

  return {
    ...mutation,
    createWithdrawal: mutation.mutate,
  };
}

// =============================================================================
// Conversions Hook
// =============================================================================

export interface UseRefereeConversionsOptions {
  fetchOnMount?: boolean;
  limit?: number;
  onSuccess?: (data: RefereeConversionsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching token conversion history
 */
export function useRefereeConversions(options: UseRefereeConversionsOptions = {}) {
  const { fetchOnMount = false, limit = 50, onSuccess, onError } = options;

  const [conversions, setConversions] = useState<RefereeConversion[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const api = useApi<RefereeConversionsResponse, [number, number]>(
    (lim, off) => refereeAPI.getConversions({ limit: lim, offset: off }),
    {
      onSuccess: (data) => {
        if (offset === 0) {
          setConversions(data.conversions);
        } else {
          setConversions((prev) => [...prev, ...data.conversions]);
        }
        setHasMore(conversions.length + data.conversions.length < data.total);
        onSuccess?.(data);
      },
      onError,
    }
  );

  const fetch = useCallback(() => {
    setOffset(0);
    return api.execute(limit, 0);
  }, [api, limit]);

  const fetchMore = useCallback(() => {
    if (!hasMore || api.isLoading) return;
    const newOffset = offset + limit;
    setOffset(newOffset);
    return api.execute(limit, newOffset);
  }, [api, limit, offset, hasMore]);

  const refetch = useCallback(() => {
    setOffset(0);
    setConversions([]);
    return api.execute(limit, 0);
  }, [api, limit]);

  // Initial fetch
  if (fetchOnMount && conversions.length === 0 && !api.isLoading && !api.isSuccess) {
    fetch();
  }

  return {
    conversions,
    total: api.data?.total ?? 0,
    hasMore,
    isLoading: api.isLoading,
    error: api.error,
    fetch,
    fetchMore,
    refetch,
  };
}

// =============================================================================
// Create Token Conversion Hook
// =============================================================================

export interface UseTokenConversionOptions {
  onSuccess?: (data: TokenConversionResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating token conversion (credit to any user)
 */
export function useTokenConversion(options: UseTokenConversionOptions = {}) {
  const mutation = useMutation<TokenConversionResponse, [number, string]>(
    (amount, targetTelegramId) => refereeAPI.createTokenConversion({ amount, targetTelegramId }),
    options
  );

  return {
    ...mutation,
    convert: mutation.mutate,
    conversionResult: mutation.data,
  };
}

// =============================================================================
// Combined Referee Hook
// =============================================================================

/**
 * All-in-one referee hook
 */
export function useReferee() {
  const profile = useRefereeProfile();
  const balanceLog = useBalanceLog();
  const withdrawals = useRefereeWithdrawals();
  const conversions = useRefereeConversions();
  const createWithdrawal = useCreateRefereeWithdrawal({
    onSuccess: () => {
      profile.refetch();
      withdrawals.refetch();
    },
  });
  const tokenConversion = useTokenConversion({
    onSuccess: () => {
      profile.refetch();
      conversions.refetch();
    },
  });

  return {
    // Profile
    profile: profile.profile,
    isLoadingProfile: profile.isLoading,
    profileError: profile.error,
    refetchProfile: profile.refetch,

    // Balance Log
    balanceLogs: balanceLog.logs,
    balanceLogTotal: balanceLog.total,
    hasMoreLogs: balanceLog.hasMore,
    isLoadingLogs: balanceLog.isLoading,
    fetchLogs: balanceLog.fetch,
    fetchMoreLogs: balanceLog.fetchMore,
    refetchLogs: balanceLog.refetch,

    // Withdrawals
    withdrawals: withdrawals.withdrawals,
    withdrawalsTotal: withdrawals.total,
    hasMoreWithdrawals: withdrawals.hasMore,
    isLoadingWithdrawals: withdrawals.isLoading,
    fetchWithdrawals: withdrawals.fetch,
    fetchMoreWithdrawals: withdrawals.fetchMore,
    refetchWithdrawals: withdrawals.refetch,

    // Create Withdrawal
    createWithdrawal: createWithdrawal.createWithdrawal,
    isCreatingWithdrawal: createWithdrawal.isLoading,
    createWithdrawalError: createWithdrawal.error,

    // Conversions
    conversions: conversions.conversions,
    conversionsTotal: conversions.total,
    hasMoreConversions: conversions.hasMore,
    isLoadingConversions: conversions.isLoading,
    fetchConversions: conversions.fetch,
    fetchMoreConversions: conversions.fetchMore,
    refetchConversions: conversions.refetch,

    // Token Conversion
    convertToTokens: tokenConversion.convert,
    isConverting: tokenConversion.isLoading,
    conversionResult: tokenConversion.conversionResult,
    conversionError: tokenConversion.error,
  };
}
